import { Injectable } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { AppContextSelector, DEFAULT_PAGE_CONTENT_TYPE, PageContentSegment } from "@obg/ngx.core";
import { TranslationService } from "@obg/ngx.core.content";
import { ignoreNil } from "@obg/ngx.global";
import { Route, RouteSelector, RouteService } from "@obg/ngx.router";
import { Observable } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";

import { GameExtended } from "../game/game.model";
import { GameSelector } from "../game/game.selector";
import { GameCollectionConfigService } from "../game-collection/game-collection.config";
import { GameCollection } from "../game-collection/game-collection.model";
import { GameCollectionSelector } from "../game-collection/game-collection.selector";
import { GamingRoutes } from "../route";
import { isRouteGameCategory, isRouteGameLauncher } from "../router/gaming-route.util";
import { GamingExternalState } from "../state";
import { FavouriteGamesSelector } from "../user-games/favourite-games/favourite-games.selector";
import { RecommendedGamesSelector } from "../user-games/recommended-games/recommended-games.selector";

@Injectable({
	providedIn: "root",
})
export class GamingRouteService {
	constructor(
		private store: Store<GamingExternalState>,
		private gameCollectionSelector: GameCollectionSelector,
		private gameSelector: GameSelector,
		private routeService: RouteService,
		private favoriteGamesSelector: FavouriteGamesSelector,
		private recommendedGamesSelector: RecommendedGamesSelector,
		private translationService: TranslationService,
		private gameCollectionConfigService: GameCollectionConfigService
	) {}

	getDefaultProductCollection$(): Observable<GameCollection> {
		return this.store.select(AppContextSelector.getProduct).pipe(
			ignoreNil(),
			map((product) => this.gameCollectionConfigService.getCollectionNameByProduct(product)),
			switchMap((collectionName) => this.store.pipe(this.gameCollectionSelector.getById$(collectionName)))
		);
	}

	getGameCollectionByCurrentRoute$(): Observable<GameCollection> {
		return this.store.pipe(
			RouteSelector.getCurrent$,
			// TODO: refactor favorite games logic so that this logic can be simpler OBGB-9580
			switchMap((route) => {
				if (route.name.endsWith(GamingRoutes.Favorites)) {
					return this.store.pipe(
						this.favoriteGamesSelector.getAll$(),
						map(
							(games) =>
								({
									name: this.translationService.getByKey("my-casino.favorites"),
									games,
								}) as GameCollection
						)
					);
				}

				if (route.name.endsWith(GamingRoutes.Recommended)) {
					return this.store.pipe(
						this.recommendedGamesSelector.getAll$(),
						map(
							(games) =>
								({
									id: "RecommendedGames",
									name: this.translationService.getByKey("game-collections.Recommended"),
									games,
								}) as GameCollection
						)
					);
				}

				if (route?.params?.category) {
					return this.store.pipe(this.gameCollectionSelector.getBySlug$(route.params.category));
				}
				return this.getDefaultProductCollection$();
			})
		);
	}

	getCategoryKey(categorySlug: string): PageContentSegment {
		let category: GameCollection | undefined;

		this.store
			.pipe(
				select(this.gameCollectionSelector.getBySlug(categorySlug)),
				tap((x) => (category = x))
			)
			.subscribe()
			.unsubscribe();

		const categoryId = category ? category.id : categorySlug;
		return { providerId: "gamecollection", key: categoryId };
	}

	getGameKey(gameSlug: string): PageContentSegment {
		let game: GameExtended | undefined;

		this.store
			.pipe(
				select(this.gameSelector.getBySlug(gameSlug)),
				tap((g: GameExtended) => (game = g))
			)
			.subscribe()
			.unsubscribe();

		const key = game?.contentKey || game?.id || gameSlug;

		return { providerId: "game", key };
	}

	// todo: move logic into GameRouteSelector
	// todo: refactor in OBGD-987
	getDocumentKeyfromRoute(route: Route): PageContentSegment | undefined {
		if (!route.params) {
			return undefined;
		}

		if (route.params.game) {
			return this.getGameKey(route.params.game);
		}

		if (route.params.category) {
			return this.getCategoryKey(route.params.category);
		}

		return undefined;
	}

	getParentSegments(route: Route): PageContentSegment[] {
		const parentNames = this.routeService.getParentNames(route.name);

		return parentNames.map((parentName) => {
			const defaultParentName = { providerId: DEFAULT_PAGE_CONTENT_TYPE, key: parentName };

			if (!route.params) {
				return defaultParentName;
			}

			if (isRouteGameLauncher(parentName)) {
				return this.getGameKey(route.params!.game);
			}

			if (isRouteGameCategory(parentName)) {
				return this.getCategoryKey(route.params!.category);
			}

			return defaultParentName;
		});
	}
}
